import { useState, useEffect } from 'react'
import { fmtDate } from '../../utils/format'
import {
  BellAlertIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BeakerIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  CogIcon,
  ChevronRightIcon,
  XMarkIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'

interface DashboardMetrics {
  temperature_controls: {
    overdue: number
    today: number
    this_week: number
  }
  cleanings: {
    overdue: number
    today: number
    this_week: number
  }
  maintenances: {
    overdue: number
    today: number
    this_week: number
  }
  upcoming_tasks: UpcomingTask[]
}

interface UpcomingTask {
  id: number
  type: 'temperature' | 'cleaning' | 'maintenance'
  title: string
  description: string
  due_date: string
  priority: 'low' | 'medium' | 'high' | 'critical'
  is_overdue: boolean
}

interface AlertSettings {
  email_enabled: boolean
  email_address: string
  telegram_enabled: boolean
  telegram_chat_id: string
  overdue_alerts: boolean
  daily_reminders: boolean
  weekly_summary: boolean
}

const API_BASE_URL = '/api/v1/dev'

export default function Dashboard() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [showAlertConfig, setShowAlertConfig] = useState(false)
  const [alertSettings, setAlertSettings] = useState<AlertSettings>({
    email_enabled: false,
    email_address: '',
    telegram_enabled: false,
    telegram_chat_id: '',
    overdue_alerts: true,
    daily_reminders: false,
    weekly_summary: false
  })

  // Fetch dashboard metrics
  const fetchMetrics = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE_URL}/haccp/dashboard`)
      const data = await response.json()

      if (response.ok) {
        setMetrics(data.data)
      } else {
        console.error('Error response from dashboard:', data)
        // Set empty metrics to allow component to render
        setMetrics({
          temperature_controls: { overdue: 0, today: 0, this_week: 0 },
          cleanings: { overdue: 0, today: 0, this_week: 0 },
          maintenances: { overdue: 0, today: 0, this_week: 0 },
          upcoming_tasks: []
        })
      }
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err)
      // Set empty metrics to allow component to render
      setMetrics({
        temperature_controls: { overdue: 0, today: 0, this_week: 0 },
        cleanings: { overdue: 0, today: 0, this_week: 0 },
        maintenances: { overdue: 0, today: 0, this_week: 0 },
        upcoming_tasks: []
      })
    } finally {
      setLoading(false)
    }
  }

  // Fetch alert settings
  const fetchAlertSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/haccp/alert-settings`)

      if (!response.ok) {
        console.error('Error response from alert-settings:', response.status)
        return
      }

      const data = await response.json()

      if (data.data) {
        setAlertSettings(data.data)
      }
    } catch (err) {
      console.error('Error fetching alert settings:', err)
    }
  }

  // Save alert settings
  const saveAlertSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/haccp/alert-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(alertSettings)
      })

      if (response.ok) {
        setShowAlertConfig(false)
      }
    } catch (err) {
      console.error('Error saving alert settings:', err)
    }
  }

  useEffect(() => {
    fetchMetrics()
    fetchAlertSettings()
  }, [])

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800 border-gray-200',
      medium: 'bg-blue-100 text-blue-800 border-blue-200',
      high: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      critical: 'bg-red-100 text-red-800 border-red-200'
    }
    return colors[priority as keyof typeof colors] || colors.medium
  }

  const getTaskIcon = (type: string) => {
    const icons = {
      temperature: BeakerIcon,
      cleaning: SparklesIcon,
      maintenance: WrenchScrewdriverIcon
    }
    return icons[type as keyof typeof icons] || BeakerIcon
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Panel de Control APPCC</h1>
          <p className="mt-1 text-sm text-gray-500">
            Resumen de tareas pendientes y alertas del sistema
          </p>
        </div>
        <button
          onClick={() => setShowAlertConfig(true)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <CogIcon className="h-5 w-5 mr-2" />
          Configurar Alertas
        </button>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Temperature Controls */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="bg-blue-100 rounded-lg p-3">
                <BeakerIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">Temperaturas</h3>
            </div>
          </div>

          <div className="space-y-3">
            {metrics && metrics.temperature_controls.overdue > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <span className="text-sm font-medium text-red-900">Atrasados</span>
                </div>
                <span className="text-lg font-bold text-red-600">
                  {metrics.temperature_controls.overdue}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-yellow-600 mr-2" />
                <span className="text-sm font-medium text-yellow-900">Hoy</span>
              </div>
              <span className="text-lg font-bold text-yellow-600">
                {metrics?.temperature_controls.today || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-900">Esta Semana</span>
              </div>
              <span className="text-lg font-bold text-blue-600">
                {metrics?.temperature_controls.this_week || 0}
              </span>
            </div>
          </div>

          <button
            onClick={() => window.location.href = '/haccp/temperature-records'}
            className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Registrar Temperatura
            <ChevronRightIcon className="ml-2 h-4 w-4" />
          </button>
        </div>

        {/* Cleaning Tasks */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="bg-green-100 rounded-lg p-3">
                <SparklesIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">Limpiezas</h3>
            </div>
          </div>

          <div className="space-y-3">
            {metrics && metrics.cleanings.overdue > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <span className="text-sm font-medium text-red-900">Atrasados</span>
                </div>
                <span className="text-lg font-bold text-red-600">
                  {metrics.cleanings.overdue}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-yellow-600 mr-2" />
                <span className="text-sm font-medium text-yellow-900">Hoy</span>
              </div>
              <span className="text-lg font-bold text-yellow-600">
                {metrics?.cleanings.today || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-green-900">Esta Semana</span>
              </div>
              <span className="text-lg font-bold text-green-600">
                {metrics?.cleanings.this_week || 0}
              </span>
            </div>
          </div>

          <button
            onClick={() => window.location.href = '/haccp/cleaning-by-zone'}
            className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
          >
            Registrar Limpieza
            <ChevronRightIcon className="ml-2 h-4 w-4" />
          </button>
        </div>

        {/* Maintenance Tasks */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="bg-orange-100 rounded-lg p-3">
                <WrenchScrewdriverIcon className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">Mantenimientos</h3>
            </div>
          </div>

          <div className="space-y-3">
            {metrics && metrics.maintenances.overdue > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <span className="text-sm font-medium text-red-900">Atrasados</span>
                </div>
                <span className="text-lg font-bold text-red-600">
                  {metrics.maintenances.overdue}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-yellow-600 mr-2" />
                <span className="text-sm font-medium text-yellow-900">Hoy</span>
              </div>
              <span className="text-lg font-bold text-yellow-600">
                {metrics?.maintenances.today || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-orange-600 mr-2" />
                <span className="text-sm font-medium text-orange-900">Esta Semana</span>
              </div>
              <span className="text-lg font-bold text-orange-600">
                {metrics?.maintenances.this_week || 0}
              </span>
            </div>
          </div>

          <button
            onClick={() => window.location.href = '/haccp/maintenance'}
            className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
          >
            Registrar Mantenimiento
            <ChevronRightIcon className="ml-2 h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Upcoming Tasks */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Próximas Tareas de la Semana</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {metrics?.upcoming_tasks && metrics.upcoming_tasks.length > 0 ? (
            metrics.upcoming_tasks.map((task) => {
              const Icon = getTaskIcon(task.type)
              return (
                <div key={`${task.type}-${task.id}`} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className={`p-3 rounded-lg ${
                        task.type === 'temperature' ? 'bg-blue-100' :
                        task.type === 'cleaning' ? 'bg-green-100' : 'bg-orange-100'
                      }`}>
                        <Icon className={`h-6 w-6 ${
                          task.type === 'temperature' ? 'text-blue-600' :
                          task.type === 'cleaning' ? 'text-green-600' : 'text-orange-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-gray-900">{task.title}</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                            {task.priority === 'critical' ? 'Crítico' :
                             task.priority === 'high' ? 'Alta' :
                             task.priority === 'medium' ? 'Media' : 'Baja'}
                          </span>
                          {task.is_overdue && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                              <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                              Atrasado
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Vence: {fmtDate(task.due_date)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const urls = {
                          temperature: '/haccp/temperature-records',
                          cleaning: '/haccp/cleaning-by-zone',
                          maintenance: '/haccp/maintenance'
                        }
                        window.location.href = urls[task.type]
                      }}
                      className="ml-4 inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Registrar
                      <ChevronRightIcon className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="p-8 text-center text-gray-500">
              <BellAlertIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <p>No hay tareas pendientes para esta semana</p>
            </div>
          )}
        </div>
      </div>

      {/* Alert Configuration Modal */}
      {showAlertConfig && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-lg w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Configuración de Alertas</h3>
                  <button
                    onClick={() => setShowAlertConfig(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Email Notifications */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">Email</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={alertSettings.email_enabled}
                          onChange={(e) => setAlertSettings({ ...alertSettings, email_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                    {alertSettings.email_enabled && (
                      <input
                        type="email"
                        value={alertSettings.email_address}
                        onChange={(e) => setAlertSettings({ ...alertSettings, email_address: e.target.value })}
                        className="mt-2 block w-full border-gray-300 rounded-md shadow-sm text-sm"
                        placeholder="correo@ejemplo.com"
                      />
                    )}
                  </div>

                  {/* Telegram Notifications */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <BellAlertIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">Telegram</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={alertSettings.telegram_enabled}
                          onChange={(e) => setAlertSettings({ ...alertSettings, telegram_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                    {alertSettings.telegram_enabled && (
                      <input
                        type="text"
                        value={alertSettings.telegram_chat_id}
                        onChange={(e) => setAlertSettings({ ...alertSettings, telegram_chat_id: e.target.value })}
                        className="mt-2 block w-full border-gray-300 rounded-md shadow-sm text-sm"
                        placeholder="Chat ID de Telegram"
                      />
                    )}
                  </div>

                  {/* Alert Types */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">Tipos de Alertas</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={alertSettings.overdue_alerts}
                        onChange={(e) => setAlertSettings({ ...alertSettings, overdue_alerts: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Alertas de tareas atrasadas</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={alertSettings.daily_reminders}
                        onChange={(e) => setAlertSettings({ ...alertSettings, daily_reminders: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Recordatorios diarios</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={alertSettings.weekly_summary}
                        onChange={(e) => setAlertSettings({ ...alertSettings, weekly_summary: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Resumen semanal</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={saveAlertSettings}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Guardar
                </button>
                <button
                  onClick={() => setShowAlertConfig(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
